/**
 * Voice handlers for per-notification actions inside the open
 * Notifications panel (NotificationModal.jsx): mark as read, mark as
 * unread. Popup-scoped to "notifications_modal" so they're only
 * advertised while the panel is open.
 *
 * Notifications have no naturally-spoken identifier, so — mirroring
 * useCopilotEncountersPopoverActions' `select_encounter` — items are
 * addressed by 1-based `rank`, top to bottom, matching the order
 * actually rendered by NotificationList/groupNotificationsByDate (the
 * `notifications` array passed in must be in that same, already-grouped
 * order; NotificationModal passes its `displayedNotifications` memo).
 *
 * Both handlers share the exact same `useNotificationActions` callbacks
 * (`handleMarkAsRead` / `handleMarkAsUnread`) the manual UI uses — the
 * "Unread" button visible on a read notification, and the auto-mark
 * that runs when a notification is opened/clicked. No new business
 * logic; this only exposes the existing handlers to voice.
 */

import { useMemo } from "react";

import { useCopilotAction } from "./actionRegistry";

const SCOPE = "notifications_modal";

const MARK_READ_DESCRIPTOR = Object.freeze({
  name: "mark_notification_as_read",
  description:
    "Mark a notification in the currently-open Notifications panel as " +
    "read, by its 1-based position in the visible list (top to bottom). " +
    "Implemented by noki-fe (useNotificationActions.handleMarkAsRead). " +
    "Use when the user says: 'mark the first notification as read', " +
    "'mark that as read'. Only while the Notifications panel is open.",
  classification: "WRITE_SAFE",
  confirmStyle: "none",
  parameters: [
    {
      name: "rank",
      type: "integer",
      description: "1-based position of the notification in the currently visible list, top to bottom.",
      required: true,
    },
  ],
});

const MARK_UNREAD_DESCRIPTOR = Object.freeze({
  name: "mark_notification_as_unread",
  description:
    "Mark a notification in the currently-open Notifications panel as " +
    "unread, by its 1-based position in the visible list (top to bottom) " +
    "— the same as clicking its 'Unread' button. Implemented by noki-fe " +
    "(useNotificationActions.handleMarkAsUnread). Use when the user says: " +
    "'mark the second notification as unread', 'mark that unread'. Only " +
    "while the Notifications panel is open.",
  classification: "WRITE_SAFE",
  confirmStyle: "none",
  parameters: [
    {
      name: "rank",
      type: "integer",
      description: "1-based position of the notification in the currently visible list, top to bottom.",
      required: true,
    },
  ],
});

const resolveByRank = (notifications, rank) => {
  const list = Array.isArray(notifications) ? notifications : [];
  if (list.length === 0) {
    throw new Error(
      "blocked_by_state: no_notifications — there are no notifications currently visible.",
    );
  }
  if (!Number.isInteger(rank)) {
    throw new Error("validation_failed: 'rank' (1-based, integer) is required.");
  }
  if (rank < 1 || rank > list.length) {
    throw new Error(
      `blocked_by_state: no_match — rank ${rank} is out of range; ${list.length} notification(s) are currently visible.`,
    );
  }
  return list[rank - 1];
};

export function useCopilotNotificationItemActions({
  notifications,
  onMarkAsRead,
  onMarkAsUnread,
}) {
  const markReadDescriptor = useMemo(() => MARK_READ_DESCRIPTOR, []);
  const markUnreadDescriptor = useMemo(() => MARK_UNREAD_DESCRIPTOR, []);

  useCopilotAction(
    markReadDescriptor,
    async (args) => {
      // eslint-disable-next-line no-console
      console.info("[copilot] mark_notification_as_read.invoke", { args });
      const target = resolveByRank(notifications, args?.rank);
      if (target.type !== "background_task") {
        throw new Error(
          "blocked_by_state: this notification can't be marked read/unread.",
        );
      }
      if (target.isRead) {
        return { rank: args.rank, already_read: true };
      }
      if (typeof onMarkAsRead !== "function") {
        throw new Error("host_exception: mark-as-read handler not wired.");
      }
      await onMarkAsRead(target);
      return { rank: args.rank, marked_read: true };
    },
    { scope: SCOPE },
  );

  useCopilotAction(
    markUnreadDescriptor,
    async (args) => {
      // eslint-disable-next-line no-console
      console.info("[copilot] mark_notification_as_unread.invoke", { args });
      const target = resolveByRank(notifications, args?.rank);
      if (target.type !== "background_task") {
        throw new Error(
          "blocked_by_state: this notification can't be marked read/unread.",
        );
      }
      if (!target.isRead) {
        return { rank: args.rank, already_unread: true };
      }
      if (typeof onMarkAsUnread !== "function") {
        throw new Error("host_exception: mark-as-unread handler not wired.");
      }
      await onMarkAsUnread(target);
      return { rank: args.rank, marked_unread: true };
    },
    { scope: SCOPE },
  );
}
